import React from "react";
import { Link } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { colors } from "./colors";
import { PageContainer } from "./PageContainer";

const MotionBox = motion(Box);

const HERO_BG = "#080808";
const RING_RADIUS = 140;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;
const TRUST_SCORE = 92;

const REVEAL = {
  initial: { opacity: 0, y: 28, filter: "blur(10px)" },
  whileInView: { opacity: 1, y: 0, filter: "blur(0px)" },
  viewport: { once: true, amount: 0.25 },
};

const SECTION_SX = {
  textAlign: "center",
  paddingTop: "8rem",
  paddingBottom: "8rem",
  px: { xs: 2, md: 4 },
  backgroundColor: HERO_BG,
};

const btnGhostSx = {
  fontFamily: "'Inter', system-ui, sans-serif",
  fontSize: "13px",
  fontWeight: 600,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: colors.text.secondary,
  px: "32px",
  py: "13px",
  textDecoration: "none",
  border: "1px solid rgba(255,255,255,0.08)",
  background: "linear-gradient(180deg, rgba(255,255,255,0.01), transparent)",
  position: "relative",
  overflow: "hidden",
  display: "inline-block",
  transition:
    "transform .25s cubic-bezier(.22,1,.36,1), border-color .25s ease, color .25s ease, box-shadow .25s ease, background .25s ease",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: "15%",
    right: "15%",
    height: "1px",
    background: "linear-gradient(to right, transparent, rgba(255,255,255,0.18), transparent)",
    opacity: 0,
    transition: "opacity .25s ease",
  },
  "&:hover": {
    color: colors.text.primary,
    borderColor: "rgba(232,163,61,0.25)",
    background: "linear-gradient(180deg, rgba(232,163,61,0.04), rgba(232,163,61,0.02))",
    boxShadow: "0 8px 24px rgba(232,163,61,0.08)",
    transform: "translateY(-2px)",
  },
  "&:hover::before": { opacity: 1 },
  "&:active": { transform: "translateY(0px)" },
};

const LABEL_SX = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "0.8rem",
  mb: 1.5,
  fontSize: "0.65rem",
  fontWeight: 600,
  letterSpacing: "0.25em",
  textTransform: "uppercase",
  color: colors.primary.main,
  "&::before": {
    content: '""',
    display: "block",
    width: 24,
    height: "1px",
    background: colors.primary.main,
  },
};

const TITLE_SX = {
  fontFamily: "'Inter', system-ui, sans-serif",
  fontSize: { xs: "2.25rem", sm: "2.75rem", md: "3.75rem" },
  fontWeight: 700,
  letterSpacing: "-0.04em",
  lineHeight: 0.95,
  textTransform: "uppercase",
  color: colors.text.primary,
  mx: "auto",
  maxWidth: "28rem",
  "& span": {
    background: "linear-gradient(180deg, #f7cb79 0%, #f0b458 40%, #de8f2c 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },
};

const RING_WRAP_SX = {
  position: "relative",
  width: { xs: 260, md: 320 },
  height: { xs: 260, md: 320 },
  mx: "auto",
  mt: { xs: "3rem", md: "5rem" },
  mb: { xs: "2rem", md: "2.5rem" },
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const SCORE_NUM_SX = {
  fontFamily: "'Inter', system-ui, sans-serif",
  fontSize: { xs: "4.5rem", md: "6rem" },
  fontWeight: 700,
  lineHeight: 1,
  color: colors.text.primary,
  position: "relative",
  zIndex: 2,
};

const SCORE_SUB_SX = {
  fontSize: "0.7rem",
  fontWeight: 600,
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  color: colors.primary.main,
  position: "relative",
  zIndex: 2,
};

const SCORE_LABEL_SX = {
  mt: 0.5,
  fontSize: "0.65rem",
  fontWeight: 600,
  letterSpacing: "0.2em",
  textTransform: "uppercase",
  color: colors.text.muted,
  position: "relative",
  zIndex: 2,
};

const ATTRS_SX = {
  display: "flex",
  flexWrap: "nowrap",
  justifyContent: "center",
  alignItems: "flex-start",
  gap: { xs: "1rem", sm: "1.35rem", md: "1.5rem" },
  width: "fit-content",
  maxWidth: "100%",
  mx: "auto",
};

const ATTR_SX = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 0.5,
  flex: "0 0 auto",
  width: { xs: "4.25rem", sm: "5.25rem", md: "7.5rem" },
};

const ATTR_VAL_SX = {
  fontFamily: "'Inter', system-ui, sans-serif",
  fontSize: { xs: "1.25rem", sm: "1.4rem", md: "1.6rem" },
  fontWeight: 700,
  color: colors.text.primary,
};

const ATTR_BAR_SX = {
  width: "100%",
  maxWidth: 60,
  height: 2,
  mt: 0.25,
  position: "relative",
  backgroundColor: "rgba(255,255,255,0.08)",
};

const ATTR_LABEL_SX = {
  width: "100%",
  fontSize: { xs: "0.52rem", sm: "0.58rem", md: "0.62rem" },
  fontWeight: 500,
  letterSpacing: { xs: "0.06em", md: "0.1em" },
  textTransform: "uppercase",
  color: colors.text.muted,
  textAlign: "center",
  lineHeight: 1.35,
};

const TRUST_ATTRS = [
  { key: "sportsmanship", label: "Sportsmanship", value: 85 },
  { key: "integrity", label: "Integrity", value: 91 },
  { key: "communication", label: "Communication", value: 94 },
  { key: "team-play", label: "Team Play", value: 88 },
  { key: "reliability", label: "Reliability", value: 91 },
  { key: "report-quality", label: "Report Quality", value: 96 },
];

function TrustRing() {
  const arcOffset = RING_CIRCUMFERENCE * (1 - TRUST_SCORE / 100);

  return (
    <Box sx={RING_WRAP_SX}>
      <Box
        component="svg"
        viewBox="0 0 320 320"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        sx={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
      >
        <circle cx="160" cy="160" r={RING_RADIUS} stroke="rgba(255,255,255,0.05)" strokeWidth="1" />
        <motion.circle
          cx="160"
          cy="160"
          r={RING_RADIUS}
          stroke="url(#reputationGoldArc)"
          strokeWidth="2"
          strokeLinecap="round"
          fill="none"
          transform="rotate(-90 160 160)"
          strokeDasharray={RING_CIRCUMFERENCE}
          initial={{ strokeDashoffset: RING_CIRCUMFERENCE }}
          whileInView={{ strokeDashoffset: arcOffset }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
        />
        <circle cx="160" cy="160" r="110" stroke="rgba(200,135,42,0.12)" strokeWidth="0.5" />
        <circle cx="160" cy="160" r="80" stroke="rgba(200,135,42,0.07)" strokeWidth="0.5" />
        <defs>
          <linearGradient id="reputationGoldArc" x1="0" y1="0" x2="320" y2="0">
            <stop offset="0%" stopColor="rgba(200,135,42,0.4)" />
            <stop offset="50%" stopColor="#C8872A" />
            <stop offset="100%" stopColor="#E8A83A" />
          </linearGradient>
        </defs>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <Typography component="div" sx={SCORE_NUM_SX}>
          {TRUST_SCORE}
        </Typography>
        <Typography component="div" sx={SCORE_SUB_SX}>
          / 100
        </Typography>
        <Typography component="div" sx={SCORE_LABEL_SX}>
          Trust Score
        </Typography>
      </Box>
    </Box>
  );
}

function TrustAttr({ attr, index }) {
  return (
    <MotionBox
      {...REVEAL}
      transition={{
        duration: 0.9,
        delay: index * 0.08,
        ease: [0.22, 1, 0.36, 1],
      }}
      sx={ATTR_SX}
    >
      <Typography component="div" sx={ATTR_VAL_SX}>
        {attr.value}
      </Typography>
      <Box sx={ATTR_BAR_SX}>
        <MotionBox
          initial={{ width: 0 }}
          whileInView={{ width: `${attr.value}%` }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{
            duration: 0.9,
            delay: 0.15 + index * 0.08,
            ease: [0.22, 1, 0.36, 1],
          }}
          sx={{
            position: "absolute",
            left: 0,
            top: 0,
            height: "100%",
            backgroundColor: colors.primary.main,
          }}
        />
      </Box>
      <Typography component="div" sx={ATTR_LABEL_SX}>
        {attr.label}
      </Typography>
    </MotionBox>
  );
}

export default function ReputationSystem() {
  return (
    <Box component="section" id="trustscore" sx={SECTION_SX}>
      <PageContainer>
        <MotionBox {...REVEAL} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}>
          <Typography component="div" sx={LABEL_SX}>
            Reputation System
          </Typography>
          <Typography component="h2" sx={TITLE_SX}>
            Beyond the
            <br />
            <span>Scoreboard</span>
          </Typography>
          <Typography
            sx={{
              mt: 2,
              maxWidth: "34rem",
              mx: "auto",
              fontSize: "0.95rem",
              lineHeight: 1.7,
              color: "rgba(255,255,255,0.58)",
            }}
          >
            Matchmaking should evaluate more than aim. Ouro tracks consistency, integrity,
            communication and long-term player behavior.
          </Typography>
        </MotionBox>

        <MotionBox
          {...REVEAL}
          transition={{ duration: 0.9, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
        >
          <TrustRing />
        </MotionBox>

        <Box sx={ATTRS_SX}>
          {TRUST_ATTRS.map((attr, index) => (
            <TrustAttr key={attr.key} attr={attr} index={index} />
          ))}
        </Box>

        <Box sx={{ display: "flex", justifyContent: "center", mt: { xs: 5, md: 6 } }}>
          <Box component={Link} to="/reputation" sx={btnGhostSx}>
            Learn more →
          </Box>
        </Box>
      </PageContainer>
    </Box>
  );
}
